import { useEffect, useMemo } from 'react';
import Raven from 'raven-js';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { usePurchaseMotions } from '../../hooks/usePurchaseMotions';
import { trackPurchaseMotionFailure } from '../../utils/metrics';
import { ErrorBoundary, ErrorBoundaryNames } from '../ErrorBoundary';
import { PurchaseMotionCTA } from '../PurchaseMotionCTA/PurchaseMotionCTA';
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Standalone Talk to Sales button that handles purchase motion evaluation internally.
 *
 * `TALK_TO_SALES_BUTTON` is a special location that only ever returns talk-to-sales
 * purchase motions.
 *
 * Use this instead of `PurchaseMotionCTA` when you specifically want a Talk to Sales
 * entry point without needing to know or pass the purchase motion yourself.
 *
 * @see https://product.hubteam.com/docs/purchase-motions-lib/examples.html#talk-to-sales-pattern
 */
const TalkToSalesCTABase = ({
  apiName,
  children,
  uiButtonProps = {},
  onClick,
  onCancel,
  onView,
  upgradeConfig,
  isUpgradePoint,
  productSku,
  trackingData,
  'data-test-id': dataTestId,
  'data-selenium-id': dataSeleniumId
}) => {
  const mergedUiButtonProps = Object.assign({
    use: 'primary'
  }, uiButtonProps);
  const {
    loading,
    error,
    purchaseMotions
  } = usePurchaseMotions({
    apiName,
    location: 'TALK_TO_SALES_BUTTON'
  });
  useEffect(() => {
    if (error) {
      trackPurchaseMotionFailure({
        uniqueId: trackingData.uniqueId,
        apiName,
        purchaseMotion: 'TALK_TO_SALES'
      });
      Raven.captureException(error, {
        fingerprint: ['Failed to load TALK_TO_SALES_CTA purchase motion'],
        extra: {
          upgradeConfig
        }
      });
    }
  }, [apiName, error, trackingData.uniqueId, upgradeConfig]);
  const talkToSalesMotion = useMemo(() => {
    const found = purchaseMotions.find(motion => motion === 'TALK_TO_SALES');
    return found;
  }, [purchaseMotions]);
  if (loading) {
    return /*#__PURE__*/_jsx(LoadingButton, Object.assign({}, mergedUiButtonProps, {
      loading: true,
      children: children !== null && children !== void 0 ? children : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "purchaseMotions.buttons.talkToSales"
      })
    }));
  }
  if (!talkToSalesMotion) {
    return null;
  }
  return /*#__PURE__*/_jsx(PurchaseMotionCTA, {
    apiName: apiName,
    purchaseMotion: talkToSalesMotion,
    trackingData: trackingData,
    uiButtonProps: mergedUiButtonProps,
    onClick: onClick,
    onView: onView,
    upgradeConfig: upgradeConfig,
    isUpgradePoint: isUpgradePoint,
    productSku: productSku,
    "data-test-id": dataTestId,
    "data-selenium-id": dataSeleniumId,
    ctaProps: {
      onCancel
    },
    children: children
  });
};
export const TalkToSalesCTA = props => /*#__PURE__*/_jsx(ErrorBoundary, {
  boundaryName: ErrorBoundaryNames.TALK_TO_SALES_CTA,
  children: /*#__PURE__*/_jsx(TalkToSalesCTABase, Object.assign({}, props))
});